import type { ModelResult, Scenario } from "./types";
import {
  OPERATOR_AFFORDABILITY_CAUTION,
  OPERATOR_AFFORDABILITY_CEILING,
} from "./decision-thresholds";

export {
  OPERATOR_AFFORDABILITY_CAUTION,
  OPERATOR_AFFORDABILITY_CEILING,
} from "./decision-thresholds";

export type HurdleStatus = "met" | "marginal" | "below";

const MARGINAL_BAND = 0.005;
const COMPARISON_EPSILON = 1e-12;

export interface DeveloperDecision {
  achievedIrr: number;
  hurdleIrr: number;
  gap: number;
  available: boolean;
  status: HurdleStatus;
  hurdleMet: boolean;
}

export const GEARED_REFERENCE_DISCLOSURE =
  "Derived at the rent where the matched-horizon ungeared project earns the stated hurdle. It is a developer financing diagnostic; it does not establish affordability, a negotiating range, or rent entitlement.";

export interface GearedReference {
  achievedIrr: number;
  referenceIrr: number;
  available: boolean;
}

export function getGearedReference(result: ModelResult): GearedReference {
  const achievedIrr = result.kpis.developerPrimaryGearedIrr;
  const referenceIrr = result.developer.gearedHurdle;
  return {
    achievedIrr,
    referenceIrr,
    available:
      result.developer.gearedHurdleConverged && Number.isFinite(referenceIrr),
  };
}

function hurdleStatus(gap: number): HurdleStatus {
  const adjustedGap = gap + COMPARISON_EPSILON;
  if (adjustedGap >= MARGINAL_BAND) return "met";
  if (adjustedGap >= -MARGINAL_BAND) return "marginal";
  return "below";
}

export function getDeveloperDecision(
  scenario: Scenario,
  result: ModelResult,
): DeveloperDecision {
  const achievedIrr = result.kpis.developerPrimaryIrr;
  const hurdleIrr = scenario.assumptions.developerHurdleIRR;
  const available = Number.isFinite(achievedIrr) && Number.isFinite(hurdleIrr);
  const gap = available ? achievedIrr - hurdleIrr : Number.NEGATIVE_INFINITY;
  const status = hurdleStatus(gap);

  return {
    achievedIrr,
    hurdleIrr,
    gap,
    available,
    status,
    hurdleMet: gap >= 0,
  };
}

export interface PrimaryReturnView {
  ungearedLabel: string;
  ungearedIrr: number;
  ungearedStatus: HurdleStatus;
  gearedLabel: string;
  gearedIrr: number;
  gearedReferenceLabel: string;
  gearedReference: number | null;
}

export function getPrimaryReturnView(
  scenario: Scenario,
  result: ModelResult,
): PrimaryReturnView {
  const exitYear =
    scenario.assumptions.developerExitYear ?? scenario.assumptions.modelYears;
  const earlyExit = exitYear < scenario.assumptions.modelYears;
  const decision = getDeveloperDecision(scenario, result);
  const geared = getGearedReference(result);
  return {
    ungearedLabel: earlyExit
      ? `Matched ungeared achieved return · project Y${exitYear}`
      : "Matched ungeared achieved return · sale at completion",
    ungearedIrr: result.kpis.developerPrimaryIrr,
    ungearedStatus: decision.status,
    gearedLabel: earlyExit
      ? `Matched geared achieved return · project Y${exitYear}`
      : "Matched geared achieved return · sale at completion",
    gearedIrr: geared.achievedIrr,
    gearedReferenceLabel: "Cashflow-equivalent geared reference",
    gearedReference: geared.available ? geared.referenceIrr : null,
  };
}

export type OperatorDealVerdict =
  "WITHIN MODELLED GUARDRAILS" | "REVIEW REQUIRED" | "BOARD DECISION REQUIRED";

export interface OperatorDealRecommendation {
  verdict: OperatorDealVerdict;
  tone: "good" | "warn" | "bad";
  reason: string;
  developerDecision: DeveloperDecision;
}

function getCounterReason(
  covenantWarning: boolean,
  affordabilityCaution: boolean,
  hurdleStatus: HurdleStatus,
): string {
  if (covenantWarning) {
    return "DSCR is below the 1.25× target — negotiate covenant headroom";
  }
  if (affordabilityCaution) {
    return "Occupancy cost is in the caution band — negotiate rent";
  }
  if (hurdleStatus === "marginal") {
    return "Matched-horizon ungeared return is near the stated ungeared project hurdle — confirm counterparty tolerance";
  }
  return "Matched-horizon ungeared return is below the stated ungeared project hurdle — expect developer counter";
}

export function getOperatorDealRecommendation(
  scenario: Scenario,
  result: ModelResult,
): OperatorDealRecommendation {
  const developerDecision = getDeveloperDecision(scenario, result);
  const rentPctRevenueYear1 = result.kpis.rentPctRevenueYear1;
  const operatorNpv = result.kpis.reddamNpv;
  const affordabilityBreach =
    Number.isFinite(rentPctRevenueYear1) &&
    rentPctRevenueYear1 > OPERATOR_AFFORDABILITY_CEILING;
  const affordabilityCaution =
    Number.isFinite(rentPctRevenueYear1) &&
    rentPctRevenueYear1 > OPERATOR_AFFORDABILITY_CAUTION;
  const covenantBreach = result.developer.dscrStatus === "bad";
  const covenantWarning = result.developer.dscrStatus === "warn";
  const npvFailure = Number.isFinite(operatorNpv) && operatorNpv <= 0;

  const boardDecisionReasons = [
    affordabilityBreach
      ? "operator affordability internal screening threshold exceeded"
      : "",
    npvFailure ? "operator NPV is non-positive" : "",
    covenantBreach ? "developer financing coverage is below minimum" : "",
  ].filter(Boolean);

  if (boardDecisionReasons.length > 0) {
    return {
      verdict: "BOARD DECISION REQUIRED",
      tone: "bad",
      reason: `${boardDecisionReasons.join("; ")} — explicit Board decision required on funding, pricing, phasing or acceptance`,
      developerDecision,
    };
  }

  const evidenceIndeterminate =
    !Number.isFinite(rentPctRevenueYear1) ||
    !Number.isFinite(operatorNpv) ||
    !Number.isFinite(developerDecision.achievedIrr) ||
    !Number.isFinite(developerDecision.hurdleIrr);
  if (evidenceIndeterminate) {
    return {
      verdict: "REVIEW REQUIRED",
      tone: "warn",
      reason:
        "Decision evidence is unavailable or indeterminate — verify inputs before Board consideration",
      developerDecision,
    };
  }
  if (
    affordabilityCaution ||
    covenantWarning ||
    developerDecision.status !== "met"
  ) {
    return {
      verdict: "REVIEW REQUIRED",
      tone: "warn",
      reason: getCounterReason(
        covenantWarning,
        affordabilityCaution,
        developerDecision.status,
      ),
      developerDecision,
    };
  }
  const dscrReason =
    result.developer.dscrStatus === "na"
      ? "DSCR is not applicable (no term-debt covenant)"
      : "DSCR is healthy";
  return {
    verdict: "WITHIN MODELLED GUARDRAILS",
    tone: "good",
    reason: `No modelled affordability breach is identified, matched-horizon ungeared return meets the stated hurdle, and ${dscrReason}`,
    developerDecision,
  };
}

export function getOperatorBoardDecisionContext(
  recommendation: OperatorDealRecommendation,
  result: ModelResult,
): string {
  if (recommendation.verdict === "BOARD DECISION REQUIRED") {
    const liquidity = Number.isFinite(result.kpis.reservesRequiredToSurvive)
      ? `${result.kpis.reservesRequiredToSurvive.toLocaleString("en-AU", {
          style: "currency",
          currency: "AUD",
          maximumFractionDigits: 0,
        })} of modelled opening liquidity support`
      : "modelled opening liquidity support";
    return `The Board may approve ${liquidity} if strategically justified, or require rent relief, phasing or other funding changes. The model does not choose among those options.`;
  }
  if (recommendation.verdict === "REVIEW REQUIRED") {
    return "Review rent, ramp, funding and counterparty terms before approval. The model presents evidence and does not make the transaction decision.";
  }
  return "No modelled guardrail breach is identified. Commercial, legal and execution diligence still govern the Board decision.";
}

/**
 * Issue-level management posture for a hard operator guardrail breach.
 * This is separate from the model verdict: it grants authority to keep
 * negotiating, never authority to execute the lease.
 */
export function getOperatorManagementPosture(
  recommendation: OperatorDealRecommendation,
): string | null {
  if (recommendation.verdict !== "BOARD DECISION REQUIRED") return null;
  return "COUNTER / DEFER — authority sought for continued negotiation only; no lease execution authority";
}
